// L-82 (WP-40): how an ordered size or weight option is described to the customer, the same
// everywhere (product page hint, basket, checkout summary, My Order, balance page, the email).
// A copy of worker/src/lib/sizeText.ts `describeSize`; keep the two the same. The Worker sends
// `sizeText` with basket rows, My Order items and balance items; this copy is the product page's,
// and the fallback for a response without it.
//
//   kg, named option          "Large Three (about 4 kg)"
//   label states the weight   "1.5kg joint", "500g", "8oz"
//   label with brackets       "Small (4.5-5kg), about 4.5 kg"
//   kg, no label              "1.5 kg"
//   each                      "× 2", or "12 pack (× 12)"
//   each sold by weight band  "4-5kg (about 4.5 kg)"

const KG_PER = {kg: 1, kgs: 1, g: 0.001, oz: 0.028349523125, lb: 0.45359237, lbs: 0.45359237};

export function cleanLabel(label) {
    if (typeof label !== 'string') return null;
    const s = label.replace(/\s+/g, ' ').trim();
    return s === '' ? null : s.slice(0, 120);
}

function formatNumber(size) {
    const raw = size === null || size === undefined ? '' : String(size).trim();
    const n = Number(raw);
    if (raw === '' || !Number.isFinite(n)) return raw;
    return String(Math.round(n * 1000) / 1000);
}

function looksLikeWeight(label) {
    return /\d\s*kg/i.test(label);
}

function statedKg(label) {
    const numbers = label.match(/\d+(?:\.\d+)?|\.\d+/g) || [];
    if (numbers.length !== 1) return null;
    const m = label.match(/(\d+(?:\.\d+)?|\.\d+)\s*(kgs?|g|oz|lbs?)\b/i);
    if (!m) return null;
    return Number(m[1]) * KG_PER[m[2].toLowerCase()];
}

export function describeSize(unit, size, label) {
    const name = cleanLabel(label);
    const num = formatNumber(size);
    const numeric = num !== '' && Number.isFinite(Number(num));
    const isEach = unit === 'each';
    const weighed = !isEach || (name !== null && looksLikeWeight(name));

    if (weighed) {
        const u = isEach || typeof unit !== 'string' || unit.trim() === '' ? 'kg' : unit.trim();
        const amount = numeric ? `${num} ${u}` : num;
        if (name === null) return amount;
        if (!numeric || name === num) return name;
        if (u === 'kg') {
            const kg = statedKg(name);
            if (kg !== null && Math.abs(kg - Number(num)) <= 0.02 * Number(num)) return name;
        }
        return name.includes('(') ? `${name}, about ${amount}` : `${name} (about ${amount})`;
    }

    const qty = numeric ? `× ${num}` : num;
    if (name === null || (numeric && /^\d+(?:\.\d+)?$/.test(name) && Number(name) === Number(num))) return qty;
    if (!numeric) return name;
    return `${name} (${qty})`;
}

/** An allocated (actual) weight or quantity: "4.12 kg", "× 2"; '' when there is none. */
export function describeAmount(unit, amount) {
    if (amount === null || amount === undefined || String(amount).trim() === '') return '';
    return describeSize(unit === 'each' ? 'each' : unit, amount, null);
}
